import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, RefreshCw, Send, Edit3, Eye, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { AnnouncementType } from "@/hooks/useAnnouncements";

interface EmailPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcement: {
    id?: string;
    title: string;
    content: string;
    type: AnnouncementType;
    event_date?: string;
    cta_text?: string;
    cta_url?: string;
  };
  onEmailSent?: () => void;
}

export const EmailPreviewDialog = ({
  open,
  onOpenChange,
  announcement,
  onEmailSent,
}: EmailPreviewDialogProps) => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generatedContent, setGeneratedContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [subject, setSubject] = useState(announcement.title);
  const [error, setError] = useState<string | null>(null);

  const generateEmail = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const { data, error: fnError } = await supabase.functions.invoke("generate-email-content", {
        body: {
          type: announcement.type,
          title: announcement.title,
          content: announcement.content,
          event_date: announcement.event_date,
          cta_text: announcement.cta_text,
          cta_url: announcement.cta_url,
        },
      });

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      setGeneratedContent(data.content);
      setEditedContent(data.content);
      toast({ title: "Email content generated!" });
    } catch (err) {
      console.error("Failed to generate email:", err);
      const message = err instanceof Error ? err.message : "Failed to generate email";
      setError(message);
      toast({
        title: "Generation failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const sendEmail = async () => {
    const contentToSend = isEditing ? editedContent : generatedContent;
    
    if (!contentToSend.trim()) {
      toast({
        title: "No content",
        description: "Please generate or write email content first",
        variant: "destructive",
      });
      return;
    }

    const confirmed = confirm(
      "Are you sure you want to send this email to ALL users? This cannot be undone."
    );
    if (!confirmed) return;

    setIsSending(true);
    
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-announcement-email", {
        body: {
          announcementId: announcement.id || "draft",
          subject: subject,
          bodyHtml: contentToSend,
          type: announcement.type,
          event_date: announcement.event_date,
          cta_url: announcement.cta_url,
          cta_text: announcement.cta_text,
        },
      });

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      toast({
        title: "Emails are being sent!",
        description: `Sending to ${data.count} users in the background`,
      });
      
      onEmailSent?.();
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to send email:", err);
      const message = err instanceof Error ? err.message : "Failed to send email";
      toast({
        title: "Send failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const currentContent = isEditing ? editedContent : generatedContent;

  // Type badge for preview
  const typeBadge = announcement.type === 'event' ? "You're Invited" : 
                    announcement.type === 'update' ? "What's New" : 
                    announcement.type === 'tip' ? "Study Tip" : "Announcement";

  // Generate preview HTML that matches the actual email template
  const previewHtml = currentContent ? `
    <div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; padding: 24px; background: #F8F8F6;">
      <div style="max-width: 560px; margin: 0 auto;">
        <!-- Logo -->
        <div style="text-align: center; margin-bottom: 24px;">
          <img src="https://studibudi.ai/Logo/sblogo.png" alt="StudiBudi" style="height: 36px; width: auto;" />
        </div>
        
        <!-- Main Card -->
        <div style="background: white; border-radius: 16px; padding: 40px; border: 1px solid #E8E6E3;">
          <!-- Badge -->
          <p style="color: #8b5cf6; font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 16px 0; text-align: center;">
            ${typeBadge}
          </p>
          
          ${announcement.type === 'event' ? `
            <!-- Event Title -->
            <h1 style="margin: 0 0 24px 0; font-size: 24px; font-weight: 700; color: #1a1a1a; line-height: 1.3; text-align: center;">
              ${announcement.title}
            </h1>
          ` : ''}
          
          <!-- Greeting -->
          <p style="color: #1a1a1a; font-size: 15px; margin: 0 0 20px 0;">Hi {name},</p>
          
          <!-- AI-Generated Body -->
          <div style="color: #374151; font-size: 14px; line-height: 1.7;">
            ${currentContent}
          </div>
          
          <!-- Divider -->
          <div style="height: 1px; background: #E8E6E3; margin: 24px 0;"></div>
          
          ${announcement.cta_text ? `
            <!-- CTA Button -->
            <div style="text-align: center; margin-top: 20px;">
              <span style="display: inline-block; padding: 14px 36px; background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); color: white; border-radius: 12px; font-weight: 600; font-size: 15px; box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);">
                ${announcement.cta_text} →
              </span>
            </div>
          ` : ''}
          
          ${announcement.type === 'event' && announcement.event_date ? `
            <!-- Calendar Links Preview -->
            <div style="text-align: center; margin-top: 20px;">
              <p style="font-size: 12px; color: #64748b; margin: 0 0 12px 0;">Add to your calendar:</p>
              <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
                <span style="padding: 6px 12px; background: #f8fafc; color: #475569; font-size: 11px; font-weight: 500; border-radius: 6px; border: 1px solid #e2e8f0;">Google</span>
                <span style="padding: 6px 12px; background: #f8fafc; color: #475569; font-size: 11px; font-weight: 500; border-radius: 6px; border: 1px solid #e2e8f0;">Outlook</span>
                <span style="padding: 6px 12px; background: #f8fafc; color: #475569; font-size: 11px; font-weight: 500; border-radius: 6px; border: 1px solid #e2e8f0;">Apple</span>
              </div>
            </div>
          ` : ''}
        </div>
        
        <!-- Footer -->
        <div style="text-align: center; padding: 24px 0;">
          <p style="margin: 0 0 4px 0; font-size: 12px; color: #64748b;">Happy learning 📚</p>
          <p style="margin: 0; font-size: 11px; color: #94a3b8;">© 2026 StudiBudi</p>
        </div>
      </div>
    </div>
  ` : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Email Preview & Send
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Subject Line */}
          <div className="space-y-2">
            <Label>Email Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line..."
            />
          </div>

          {/* Generate Button */}
          {!generatedContent && (
            <div className="flex flex-col items-center py-8 px-4 border border-dashed border-border rounded-xl bg-muted/30">
              <Sparkles className="w-8 h-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-4 text-center">
                Generate AI-powered email content based on your announcement
              </p>
              <Button onClick={generateEmail} disabled={isGenerating}>
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Email Content
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Generated Content */}
          {generatedContent && (
            <>
              {/* Toggle Edit/Preview */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant={!isEditing ? "default" : "outline"}
                    size="sm"
                    onClick={() => setIsEditing(false)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant={isEditing ? "default" : "outline"}
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateEmail}
                  disabled={isGenerating}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
              </div>

              {/* Content Area */}
              {isEditing ? (
                <div className="space-y-2">
                  <Label>Email Body (HTML)</Label>
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    rows={12}
                    className="font-mono text-sm"
                    placeholder="Email body HTML..."
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Email Preview</Label>
                  <div 
                    className="border rounded-xl overflow-hidden bg-muted/30"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              )}

              {/* Send Button */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendEmail}
                  disabled={isSending || !currentContent.trim()}
                  className="bg-primary"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to All Users
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
